import React, { useEffect, useMemo } from "react";

import { Box } from "@mui/material";
import { AsyncContentMui } from "components";
import { useChartHook } from "components/reports/Chart/useChartHook";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useActionHook } from "hooks";
import { useReportsActions } from "store";
import { ChartType, DocumentReportQueries, DocumentSectionGraph } from "store/types";

type GraphProps = {
  settings: DocumentSectionGraph["report_data"]["settings"][number];
  queries: DocumentReportQueries;
  documentId: number;
  sectionId: number;
  index: number;
  totalGraphs: number;
};

export const Graph: React.FC<GraphProps> = ({ settings, queries, documentId, sectionId, index, totalGraphs }) => {
  const { getPieChartAction } = useReportsActions();

  const { data, action: getPieChart, loading: getPieChartLoading } = useActionHook(getPieChartAction);
  const isCalcField = settings.measure?.startsWith("calc-field-");

  const containerWidth = useMemo(() => {
    return totalGraphs === 1 ? 600 : 300;
  }, [totalGraphs]);

  useEffect(() => {
    getPieChart({
      ...queries,
      measures: isCalcField ? [] : ([settings.measure].filter(Boolean) as string[]),
      groupBy: [[settings.group_by.split(",")[0]?.trim() || settings.group_by]],
      calculatedFields: isCalcField ? [settings.measure?.split("calc-field-")[1] as string] : [],
      chartName: `report-document-${documentId}-section-${sectionId}-${index}`,
      chartType: settings.chart_type as ChartType,
      processKey: `report-document-${documentId}-section-${sectionId}-${index}`,
      pageType: "document",
      ...(settings.xAxis && {
        xAxis: {
          name: settings.xAxis.name.startsWith("calc-field-")
            ? settings.xAxis.name.split("calc-field-")[1]
            : settings.xAxis.name,
          type: settings.xAxis.name.startsWith("calc-field-") ? "calc" : "measure"
        }
      }),
      ...(settings.yAxis && {
        yAxis: {
          name: settings.yAxis.name.startsWith("calc-field-")
            ? settings.yAxis.name.split("calc-field-")[1]
            : settings.yAxis.name,
          type: settings.yAxis.name.startsWith("calc-field-") ? "calc" : "measure"
        }
      })
    });
  }, [settings, queries]);

  const chartOptions = useChartHook({
    chartData: data,
    setFieldValue: () => {},
    groupBy: settings.group_by
  });

  return (
    <AsyncContentMui
      spinnerProps={{ size: 75 }}
      boxProps={{ sx: { width: containerWidth, maxWidth: containerWidth, height: 290, minHeight: 290 } }}
      isLoading={getPieChartLoading}
    >
      <Box
        sx={{
          width: containerWidth,
          maxWidth: containerWidth
        }}
      >
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </Box>
    </AsyncContentMui>
  );
};

// Old options according to design

// const graphColors: string[] = [
//   "#071F35",
//   "#066471",
//   "#26A6AE",
//   "#ECD9B6",
//   "#F1C57B",
//   "#E89960",
//   "#D46535",
//   "#803C20",
//   "#CFBF84",
//   "#DBE2AD",
//   "#96C59B"
// ];

// const options: Highcharts.Options = {
//   chart: {
//     type: "pie",
//     height: 160,
//     backgroundColor: "transparent",
//     marginRight: 100
//   },
//   title: {
//     text: undefined
//   },
//   credits: {
//     enabled: false
//   },
//   exporting: {
//     enabled: false
//   },
//   plotOptions: {
//     pie: {
//       allowPointSelect: false,
//       cursor: "pointer",
//       dataLabels: {
//         enabled: false
//       },
//       showInLegend: true,
//       borderWidth: 2,
//       borderColor: "white",
//       slicedOffset: 4,
//       center: ["35%", "50%"],
//       size: "90%"
//     }
//   },
//   legend: {
//     align: "right",
//     verticalAlign: "middle",
//     layout: "vertical",
//     itemStyle: {
//       color: "#071F35",
//       fontSize: "16px",
//       fontWeight: "normal",
//       fontFamily: "Roboto"
//     },
//     symbolRadius: 4,
//     symbolHeight: 8,
//     symbolWidth: 8,
//     x: -10
//   },
//   tooltip: {
//     formatter: function () {
//       return `<div style="display: flex; align-items: center; gap: 4px;" >
//         <div style="width: 10px; height: 10px; border-radius: 50%; background-color: ${this.color};"></div>
//         <span style="font-size: 12px; font-weight: 500; color: #071F35;">${this.key}:</span>
//         <span style="font-size: 12px; font-weight: 400; color: #071F35;">${this.y}</span>
//       </div>`;
//     }
//   },
//   colors: graphColors,
//   series: [
//     {
//       type: data?.data.type as "pie",
//       data: data?.data.data
//       // data: data?.data.data.map((item, index) => ({
//       //   ...item,
//       //   color: graphColors[index % graphColors.length]
//       // }))
//     }
//   ]
// };
